import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import Footer from '../components/Footer'
import { Helmet } from 'react-helmet-async'
import { supabase } from '../config/supabase'

const Radar = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const getProfile = async () => {
      try {
        setLoading(true)
        if (!user) return

        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single()

        if (error) throw error
        
        if (data.membership === 'free') {
          navigate('/vip')
          return
        }
        
        setProfile(data)
      } catch (error) {
        console.error('Error fetching profile:', error)
      } finally {
        setLoading(false)
      }
    }

    getProfile()
  }, [user, navigate])

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>Keywords Radar Score - AI Unlocked</title>
      </Helmet>

      <main className="flex-grow">
        <div className="max-w-4xl mx-auto p-6">
          {/* แบนเนอร์ประเภทสมาชิกและปุ่มดาวน์โหลด */}
          <div className="flex justify-center items-center gap-4 mb-6">
            {loading ? (
              <div className="flex justify-center py-4">
                <svg className="animate-spin h-8 w-8 text-purple-500" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            ) : profile ? (
              <>
                <div className={`px-4 py-2 rounded-full text-sm font-medium text-white ${
                  profile.membership === 'free' ? 'bg-gray-700' :
                  profile.membership === 'vip' ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}>
                  {profile.membership === 'free' ? 'สมาชิกทั่วไป' :
                   profile.membership === 'vip' ? 'VIP Member' :
                   'Lifetime Member'}
                </div>
                <button 
                  onClick={() => window.open('https://discord.com/invite/MxgKZkydXJ', '_blank')}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-full transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  ดาวน์โหลด Extension วิเคราะห์คู่แข่ง
                </button>
              </>
            ) : (
              <p className="text-gray-400">ไม่พบข้อมูลสมาชิก</p>
            )}
          </div>

          {/* ส่วนแสดงเกณฑ์ Keywords Radar Score */}
          <div className="bg-gray-900 rounded-lg shadow-xl p-6 border border-gray-700">
            <h2 className="text-xl font-semibold mb-6">เกณฑ์ Keywords Radar Score</h2>
            <div className="space-y-6">
              {/* ง่ายมาก */}
              <div className="bg-gray-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-green-400 font-bold">ง่ายมาก</span>
                  <div className="flex">
                    {"★★★★★".split('').map((star, i) => (
                      <span key={i} className="text-yellow-400">★</span>
                    ))}
                  </div>
                </div>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li>• จำนวนผลการค้นหา ≤ 10,000 รายการ</li>
                  <li>• โอกาสติดอันดับสูง</li>
                </ul>
              </div>

              {/* ง่าย */}
              <div className="bg-gray-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-blue-400 font-bold">ง่าย</span>
                  <div className="flex">
                    {"★★★★".split('').map((star, i) => (
                      <span key={i} className="text-yellow-400">★</span>
                    ))}
                  </div>
                </div>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li>• จำนวนผลการค้นหา 10,001 - 50,000 รายการ</li>
                  <li>• การแข่งขันไม่สูงมาก</li>
                </ul>
              </div>

              {/* ปานกลาง */}
              <div className="bg-gray-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-yellow-400 font-bold">ปานกลาง</span>
                  <div className="flex">
                    {"★★★".split('').map((star, i) => (
                      <span key={i} className="text-yellow-400">★</span>
                    ))}
                  </div>
                </div>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li>• จำนวนผลการค้นหา 50,001 - 100,000 รายการ</li>
                  <li>• การแข่งขันเริ่มสูง ต้องสร้างความแตกต่าง</li>
                </ul>
              </div>

              {/* ยาก */}
              <div className="bg-gray-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-orange-400 font-bold">ยาก</span>
                  <div className="flex">
                    {"★★".split('').map((star, i) => (
                      <span key={i} className="text-yellow-400">★</span>
                    ))}
                  </div>
                </div>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li>• จำนวนผลการค้นหา 100,001 - 500,000 รายการ</li>
                  <li>• คำค้นกว้าง การแข่งขันสูง</li>
                </ul>
              </div>

              {/* ยากมาก */}
              <div className="bg-gray-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-red-400 font-bold">ยากมาก</span>
                  <div className="flex">
                    {"★".split('').map((star, i) => (
                      <span key={i} className="text-yellow-400">★</span>
                    ))}
                  </div>
                </div>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li>• จำนวนผลการค้นหา ≥ 500,001 รายการ</li>
                  <li>• คีย์เวิร์ดทั่วไป การแข่งขันสูงมาก</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default Radar